/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import {
    FormControl,
    Input,
    MenuItem,
    Select,
    ListItemText,
    CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import { useDispatch } from 'react-redux';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import HeaderCheckBoxElement from './search/listTableHeader/HeaderCheckBoxElement';
import { HeaderCheckBoxSelectListElement } from './search/listTableHeader/HeaderCheckBoxSelectList';
import Pager from '../../components/Adescom/PagerRange';
import SearchCellInputText from '../../components/Search/SearchCellInputText';
import { Check } from 'mdi-material-ui';
import I18n from '../../components/Translate/I18n';
import InputLabel from '@material-ui/core/InputLabel';
import withStyles from '@material-ui/core/styles/withStyles';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';


type SelectApiListType = {
    fullWidth?: boolean;
    classes:any;
    native?: boolean;
    optionValue: Array<any>;
    filters: Array<Filter>;
    orders: Array<Order>;
    title?: string | null;
    addEmpty?: boolean;
    emptyLabel?: string;
    iri: string;
    fieldName: string[];
    handleChange(fieldName: string, value: any): void;
    callbackResponseParser(data: any): Array<any>;
    useType: 1 | 2;
    displayValueInitial?: string | null;
};

const SelectApiList: FunctionComponent<SelectApiListType> = ({
                                                                        classes,
                                                                        fullWidth,
                                                                        native,
                                                                        optionValue,
                                                                        orders,
                                                                        filters,
                                                                        iri,
                                                                        fieldName,
                                                                        handleChange,
                                                                        emptyLabel,
                                                                        title,
                                                                        useType,
                                                                        callbackResponseParser,
                                                                        displayValueInitial,
                                                                        addEmpty,
                                                                    }): ReactElement => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);

    const [items, setItems] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [itemsPaggination, setItemsPaggination] = useState<any | null>(null);
    const [backgroundColor, setBackgroundColor] = useState('#fafafa');
    const [isOptionChecked, setIsOptionChecked] = useState<number | null>(null);
    const [optionValueLocal, setOptionValueLocal] = useState(optionValue);
    const [filterLocal, setFilterLocal] = useState(filters);
    const [displayValue] = useState<string | null>(displayValueInitial ?? '');
    const [selectOpen, setSelectOpen] = useState(false);
    const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (items === null) {
            setLoading(true); // Rozpoczynamy ładowanie
            const itemsLoader = new ItemLoaders(dispatch);
            itemsLoader.getListWithCallback(
                iri,
                'no-name-select-api-list',
                filterLocal,
                orders,
                (data: any): void => {
                    setItems(callbackResponseParser(data));
                    if (useType === 1 && optionValueLocal && optionValueLocal.length > 0) {
                        setIsOptionChecked(optionValueLocal[0]);
                    }
                    setItemsPaggination(data);
                    setLoading(false);
                },
                {
                    dispatchAfterCallback: false,
                    propagateDataToCallback: true,
                }
            );
        }
    }, [
        dispatch,
        iri,
        filterLocal,
        orders,
        items,
        callbackResponseParser,
        optionValueLocal,
        useType,
    ]);

    const searchAction = (
        fieldNameArr: string[],
        value: string | Array<number> | Array<string> | Array<boolean>
    ): void => {
        if (Array.isArray(value)) {
            setItems(null);
            return;
        }
        if (searchTimer) {
            clearTimeout(searchTimer);
        }

        setLoading(true);

        const timerId = setTimeout(() => {
            const searchValue: string = value as string;
            let newFilter: Array<Filter> = filterLocal.filter((f) => !fieldNameArr.includes(f.name));
            if (searchValue.length > 0) {
                fieldNameArr.forEach((fn) => {
                    newFilter.push({ name: fn, value: searchValue });
                });
            }
            setFilterLocal(newFilter);
            setItems(null);
        }, 1000);
        setSearchTimer(timerId);
    };

    const paginationCallback = (uri: string): void => {
        setLoading(true);
        const itemsLoader = new ItemLoaders(dispatch);
        itemsLoader.paginationCallWithCallback(
            uri,
            'no-name-select-api-list',
            (data: any): void => {
                setItems(callbackResponseParser(data));
                setItemsPaggination(data);
                setLoading(false);
            },
            {
                dispatchAfterCallback: false,
                propagateDataToCallback: true,
            }
        );
    };

    const handleChangeLocal = (checked: boolean, value: number): void => {
        const selectedCopy: Array<number> = optionValue ?? [];
        if (checked) {
            selectedCopy.push(value);
        } else {
            selectedCopy.splice(selectedCopy.indexOf(value), 1);
        }
        setBackgroundColor('#fafafa');
        setOptionValueLocal(selectedCopy);
        handleChange(fieldName.join(','), [selectedCopy]);
    };

    const onClickSingle = (id: number): void => {
        if (isOptionChecked === id) {
            setIsOptionChecked(null);
            setBackgroundColor('#fafafa');
            setOptionValueLocal([]);
            handleChange(fieldName.join(','), []);
        } else {
            setIsOptionChecked(id);
            setOptionValueLocal([id]);
            handleChange(fieldName.join(','), [id]);
        }
        setSelectOpen(false);
    };

    return (
        <FormControl fullWidth={fullWidth ?? true} className={classes.selectFormControl} >
            <InputLabel htmlFor="simple-select" classes={{ root: classes.SelectFromRestSelectLabelRoot }}>
            </InputLabel>
            <Select
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    elevation: 0,
                    getContentAnchorEl: null,
                    className: classes.selectMenu,
                }}
                classes={{
                    select: classes.select,
                    selectMenu: classes.SelectFromRestSelectMenu,
                }}
                multiple={true}
                native={native ?? false}
                value={optionValue}
                renderValue={(): string => {
                    if (optionValueLocal.length > 0 && items) {
                        const selectedItem = items.find(
                            (item) => item.value === optionValueLocal[0]
                        );
                        return selectedItem ? selectedItem.name : emptyLabel ?? '';
                    }
                    return emptyLabel ?? '';
                }}
                open={selectOpen}
                onOpen={(): void => setSelectOpen(true)}
                onClose={(): void => setSelectOpen(false)}
                input={
                    <Input
                        style={{ backgroundColor: backgroundColor }}
                        title={title ?? ''}
                        id="select-multiple-checkbox"
                    />
                }
            >
                <MenuItem
                    key={'item_search_' + fieldName}
                    dense={false}
                    onKeyDown={(e: any): void => {
                        e.stopPropagation();
                    }}
                >
                    {loading && (
                        <CircularProgress size={20} style={{ marginRight: 8 }} />
                    )}
                    <SearchCellInputText
                        inputType={'string'}
                        fullWidth={true}
                        fieldName={fieldName.join(',')}
                        searchMinIndex={1}
                        updateCallback={(fn:any, val:any):void => searchAction(fieldName, val)}
                    />
                </MenuItem>

                {addEmpty && (
                    <MenuItem
                        disabled
                        classes={{ root: classes.selectMenuItem }}
                        key={'menu_empty_value_label'}
                    >
                        <I18n trans={emptyLabel} />
                    </MenuItem>
                )}

                {items &&
                    items.map((item) => {
                        return (
                            <MenuItem
                                dense={true}
                                classes={{
                                    root: classes.selectMenuItem,
                                    selected: classes.selectMenuItemSelected,
                                }}
                                key={'item_' + fieldName + '_' + item.value}
                                onClick={(): void => {
                                    if (useType === 1) {
                                        onClickSingle(item.value);
                                    }
                                }}
                            >
                                {item.value === isOptionChecked && useType === 1 && <Check />}
                                {useType === 2 && (
                                    <>
                                        <HeaderCheckBoxElement
                                            selected={optionValue}
                                            value={{
                                                value: item.value,
                                                name: item.name,
                                            } as HeaderCheckBoxSelectListElement}
                                            handleChange={handleChangeLocal}
                                        />
                                        <ListItemText primary={item.name} />
                                    </>
                                )}
                                {useType === 1 && (
                                    <ListItemText
                                        primary={item.name}
                                        defaultValue={item.value}
                                    />
                                )}
                            </MenuItem>
                        );
                    })}

                {itemsPaggination && itemsPaggination['hydra:view'] && (
                    <Pager callback={paginationCallback} views={itemsPaggination}  />
                )}
            </Select>
        </FormControl>
    );
};
export default withStyles(oplAppStyle)(SelectApiList);
